import {AlertCircle, Check, Film, Tag, Undo2, Video, X} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {type ResolvedEntity} from '@/pages/SingleVideoSubmissionPage';
import {type Submission} from '@/api/submissions';
import {FlagIcon, FlagPicker} from '@/components/flags/Flag';
import {cn} from '@/lib/utils';
import {worstStatusBadgeClassName, worstStatusBorderClassName} from '@/lib/submissionStatusStyle';
import {statusLabel, toneBadge} from '@/lib/status';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {ItemMeta} from '@/components/shared/ItemMeta';
import {
  buildChannelItemMetadataFields,
  buildVideoItemMetadataFields
} from '@/lib/videoItemMetadata';
import {type FlagColor} from '@/api/flags';

type SubmissionTag = {
  policyViolation: string;
  status: Submission['action_status'];
  deliveredAt: string | null;
};

// One tag per already-submitted policy violation, so an analyst can see at a glance which policy
// violation(s) this channel/video was sent under vs which are still in hopper — mirrors the
// Investigations tags, which the API collapses the same way in its own status groups.
//
// Collapsed by policy violation + status rather than one tag per submission: a live row and a
// bulk-imported historic row for the same policy violation, both sent, are the ordinary case once
// history is loaded, and two identical tags say nothing the one says. The date shown is the most
// recent any contributing row carries.
export function existingSubmissionTags(
  entity: Pick<ResolvedEntity, 'existingSubmissions'>
): SubmissionTag[] {
  const tags = new Map<string, SubmissionTag>();

  for (const s of entity.existingSubmissions) {
    if (
      s.action_status !== 'sent to client' &&
      s.action_status !== 'in hopper' &&
      s.action_status !== 'delivery failed' &&
      s.action_status !== 'not sent - removed'
    ) {
      continue;
    }

    const key = `${s.policy_violation}-${s.action_status}`;
    const existing = tags.get(key);
    if (!existing) {
      tags.set(key, {
        policyViolation: s.policy_violation,
        status: s.action_status,
        deliveredAt: s.delivered_at
      });
    } else if (s.delivered_at && (!existing.deliveredAt || s.delivered_at > existing.deliveredAt)) {
      existing.deliveredAt = s.delivered_at;
    }
  }

  return [...tags.values()];
}

export function EntityThumbnailCard({
  entity,
  flag,
  note,
  onFlag,
  onNote,
  onClassify,
  dismissed = false,
  onDismiss,
  onRestore
}: {
  readonly entity: ResolvedEntity;
  readonly flag: FlagColor | null;
  readonly note: string | null;
  readonly onFlag: (color: FlagColor | null, note?: string | null) => void;
  readonly onNote: (note: string | null) => void;
  readonly onClassify: () => void;
  readonly dismissed?: boolean;
  readonly onDismiss: () => void;
  readonly onRestore: () => void;
}) {
  if (entity.loading) {
    return (
      <Card size="sm" className="justify-center">
        <CardContent className="text-muted-foreground flex aspect-video flex-col items-center justify-center gap-2">
          <Spinner />
          <span className="max-w-full truncate font-mono text-xs">{entity.raw}</span>
        </CardContent>
      </Card>
    );
  }

  if (entity.error) {
    return (
      <Alert variant="destructive" className="h-full">
        <AlertCircle />
        <AlertTitle className="font-mono font-normal break-all">{entity.raw}</AlertTitle>
        <AlertDescription>{entity.error}</AlertDescription>
      </Alert>
    );
  }

  const thumbnailUrl = entity.video?.thumbnail_url ?? entity.channel?.thumbnail_url ?? null;
  const title =
    entity.video?.title ??
    entity.channel?.title ??
    entity.video?.id ??
    entity.channel?.id ??
    entity.raw;
  const subtitle = entity.video?.channel_title ?? entity.channel?.custom_url ?? null;
  // Scoped to the resolved id, the same key VideoStatusCard uses - so translating in list view and
  // switching to grid (or back) shows the translation already fetched rather than re-fetching it.
  const itemKey = entity.video?.id ?? entity.channel?.id ?? entity.raw;
  const metadata = {
    Fields: entity.video
      ? buildVideoItemMetadataFields(entity.video)
      : entity.channel
        ? buildChannelItemMetadataFields(entity.channel)
        : []
  };

  if (!entity.video && !entity.channel) {
    return null;
  }

  const statuses = entity.existingSubmissions.map((s) => s.action_status);
  const tags = existingSubmissionTags(entity);

  return (
    <Card
      className={cn(
        'gap-0 overflow-hidden border py-0',
        worstStatusBorderClassName(statuses),
        dismissed && 'opacity-50 grayscale'
      )}>
      <div className="group bg-card relative aspect-video w-full shrink-0 overflow-hidden">
        {thumbnailUrl ? (
          <SensitiveMedia
            src={thumbnailUrl}
            alt={title}
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <div className="text-muted-foreground flex h-full items-center justify-center">
            {entity.channel ? <Video className="size-8" /> : <Film className="size-8" />}
          </div>
        )}

        {/* On top of the image, so the sanctioned black/white overlay rather than a token badge. */}
        <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
          {entity.channel ? (
            <Video className="size-3 shrink-0" />
          ) : (
            <Film className="size-3 shrink-0" />
          )}
          {entity.channel ? 'Channel' : 'Video'}
        </span>
      </div>

      <CardContent className="flex flex-1 flex-col gap-1 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-foreground line-clamp-2 min-w-0 text-xs leading-snug font-medium">
            {title}
          </p>
          {flag ? <FlagIcon color={flag} className="mt-0.5" /> : null}
        </div>
        {subtitle ? <p className="text-muted-foreground truncate text-xs">{subtitle}</p> : null}
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag) => {
              const text = `${tag.policyViolation} · ${statusLabel(tag.status)}`;
              return (
                <Badge
                  key={`${tag.policyViolation}-${tag.status}`}
                  title={
                    tag.deliveredAt
                      ? `${text} (sent to client ${new Date(tag.deliveredAt).toLocaleDateString('en-GB')})`
                      : text
                  }
                  className={cn('max-w-full', worstStatusBadgeClassName([tag.status]))}>
                  <span className="min-w-0 truncate">{text}</span>
                </Badge>
              );
            })}
          </div>
        ) : null}
        {/* Same component the investigations grid tiles use, fed the same Fields[] blob this page
            already builds for the item it submits - so "expand a result for more detail" works
            identically in both places, and the grid stops being the one view with no metadata. */}
        <ItemMeta metadata={metadata} itemKey={itemKey} title={title} />
      </CardContent>

      <CardFooter className="justify-end gap-1 p-2">
        {entity.submitted ? (
          <Badge {...toneBadge('success')}>
            <Check />
            Submitted
          </Badge>
        ) : dismissed ? (
          <>
            <Badge variant="outline">Dismissed</Badge>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onRestore();
              }}>
              <Undo2 />
              Restore
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}>
              <X />
              Dismiss
            </Button>
            <FlagPicker
              value={flag}
              note={note}
              subject={title}
              onChange={onFlag}
              onNoteChange={onNote}
            />
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onClassify();
              }}>
              <Tag />
              Classify
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}
